import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Fieldset } from 'primeng/fieldset';
import { ChangeDetectionStrategy, Component, inject, model, output, signal } from '@angular/core';

import { VersioningService } from '../../../core/services/versioning/versioning.service';
import { CurrentVersionState, UpdateAction } from '../../../core/services/versioning/versioning.interfaces';

@Component({
	selector: 'app-update-dialog',
	templateUrl: './update-dialog.component.html',
	styleUrl: './update-dialog.component.css',
	imports: [Dialog, Button, Fieldset],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateDialogComponent {
	public visible = model<boolean>(false);
	public actionTaken = output<UpdateAction>();
	public readonly isProcessing = signal<boolean>(false);

	private readonly versioningService = inject(VersioningService);

	/**
	 * Get current version state from versioning service
	 */
	public get versionState(): () => CurrentVersionState {
		return this.versioningService.versionState;
	}

	/**
	 * Get current version safely
	 */
	public get currentVersion(): string {
		const current = this.versionState().current;
		return current?.version ?? 'N/A';
	}

	/**
	 * Get latest version safely
	 */
	public get latestVersion(): string {
		const latest = this.versionState().latest;
		return latest?.version ?? 'N/A';
	}

	/**
	 * Get current build info safely
	 */
	public get currentBuildInfo(): { date: string; hash: string; build: string } {
		const current = this.versionState().current;
		if (!current) return { date: 'N/A', hash: 'N/A', build: 'N/A' };

		return {
			date: new Date(current.buildDate).toLocaleDateString('en-GB', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			}),
			hash: current.commitHash?.substring(0, 7) ?? 'N/A',
			build: current.build ?? 'N/A'
		};
	}

	/**
	 * Get latest build info safely
	 */
	public get latestBuildInfo(): { date: string; hash: string; build: string } {
		const latest = this.versionState().latest;
		if (!latest) return { date: 'N/A', hash: 'N/A', build: 'N/A' };

		return {
			date: new Date(latest.buildDate).toLocaleDateString('en-GB', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			}),
			hash: latest.commitHash?.substring(0, 7) ?? 'N/A',
			build: latest.build ?? 'N/A'
		};
	}

	/**
	 * Get release notes safely
	 */
	public get releaseNotes(): string[] {
		return this.versionState().latest?.releaseNotes ?? [];
	}

	/**
	 * Handle "Update Now" button click
	 */
	public onUpdateNow(): void {
		this.isProcessing.set(true);
		this.actionTaken.emit(UpdateAction.UPDATE_NOW);
		this.versioningService.updateNow();
	}

	/**
	 * Handle "Ask Me Later" button click
	 */
	public onAskLater(): void {
		this.isProcessing.set(true);
		this.actionTaken.emit(UpdateAction.ASK_LATER);
		this.versioningService.askMeLater();
		this.visible.set(false);
		this.isProcessing.set(false);
	}
}
